import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Define types based on main app
export interface SaleItem {
  id: string;
  type: 'product' | 'service';
  name: string;
  quantity: number;
  price: number;
  discount?: number;
  total: number;
}

export interface StaffCommission {
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  commissionRate: number;
  commissionAmount: number;
  type?: 'sale' | 'commission';
}

export type PaymentMethod = 'cash' | 'card' | 'qr' | 'bank' | 'other';

export interface Sale {
  id?: string;
  saleNumber: string;
  items: SaleItem[];
  subtotal: number;
  discount?: number;
  tax?: number;
  total: number;
  paymentMethod: PaymentMethod;
  paymentStatus: 'pending' | 'paid' | 'partially_paid';
  status?: 'active' | 'voided' | 'draft';
  voidReason?: string;
  voidedAt?: any;
  voidedBy?: string;
  voidedByName?: string;
  adminApprovedById?: string;
  adminApprovedByName?: string;
  customerId?: string;
  customerDetails?: {
    name: string;
    email?: string;
    phone?: string;
    customerId?: string;
  };
  staffId: string;
  staffName: string;
  staffCommissions?: StaffCommission[];
  notes?: string;
  createdAt?: any;
  updatedAt?: any;
  serviceSlotId?: string;
  serviceSlotNumber?: number;
}

export interface Commission {
  id?: string;
  staffId: string;
  staffName: string;
  saleId: string;
  saleNumber: string;
  serviceId: string;
  serviceName: string;
  servicePrice: number;
  commissionRate: number;
  amount: number;
  date: any;
  status: 'pending' | 'paid' | 'voided';
  paymentDate?: any;
  customerName?: string;
  type?: 'sale' | 'commission';
  createdAt?: any;
  updatedAt?: any;
}

// Convert Firestore timestamp to Date
export const convertTimestampToDate = (timestamp: any): Date => {
  if (timestamp?.toDate) {
    return timestamp.toDate();
  }
  return new Date(timestamp || Date.now());
};

// Get sales with optional date range filtering
export const getSales = async (startDate?: Date | null, endDate?: Date | null): Promise<Sale[]> => {
  try {
    const salesCollection = collection(db, 'sales');
    let salesQuery;

    if (startDate && endDate) {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      salesQuery = query(
        salesCollection,
        where('createdAt', '>=', startTimestamp),
        where('createdAt', '<=', endTimestamp),
        orderBy('createdAt', 'desc')
      );
    } else {
      salesQuery = query(
        salesCollection,
        orderBy('createdAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(salesQuery);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : new Date()
      } as Sale;
    });
  } catch (error) {
    console.error('Error fetching sales:', error);
    return [];
  }
};

// Get a single sale by ID
export const getSaleById = async (id: string): Promise<Sale | null> => {
  try {
    const saleRef = doc(db, 'sales', id);
    const docSnap = await getDoc(saleRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : new Date()
      } as Sale;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching sale:', error);
    return null;
  }
};

// Get recent sales with limit
export const getRecentSales = async (limit = 10): Promise<Sale[]> => {
  try {
    const salesCollection = collection(db, 'sales');
    const salesQuery = query(
      salesCollection,
      orderBy('createdAt', 'desc'),
      where('status', '!=', 'voided')
    );
    
    const querySnapshot = await getDocs(salesQuery);
    
    return querySnapshot.docs
      .slice(0, limit)
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : new Date(),
          updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : new Date()
        } as Sale;
      });
  } catch (error) {
    console.error('Error fetching recent sales:', error);
    return [];
  }
};

// Get commissions with optional staffId and date range filtering
export const getCommissions = async (
  staffId?: string, 
  startDate?: Date, 
  endDate?: Date,
  saleId?: string
): Promise<Commission[]> => {
  try {
    const commissionsCollection = collection(db, 'commissions');
    let commissionsQuery;
    
    // Build query filters
    const filters = [];
    
    if (staffId) {
      filters.push(where('staffId', '==', staffId));
    }
    
    if (saleId) {
      filters.push(where('saleId', '==', saleId));
    }
    
    if (startDate && endDate) {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      filters.push(where('date', '>=', startTimestamp));
      filters.push(where('date', '<=', endTimestamp));
    }
    
    if (filters.length > 0) {
      commissionsQuery = query(
        commissionsCollection,
        ...filters,
        orderBy('date', 'desc')
      );
    } else {
      commissionsQuery = query(
        commissionsCollection,
        orderBy('date', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(commissionsQuery);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: data.date ? convertTimestampToDate(data.date) : new Date(),
        paymentDate: data.paymentDate ? convertTimestampToDate(data.paymentDate) : undefined,
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : new Date()
      } as Commission;
    });
  } catch (error) {
    console.error('Error fetching commissions:', error);
    return [];
  }
};

// Get income by category (with proportional discount applied)
export const getIncomeByCategory = async (startDate?: Date, endDate?: Date): Promise<{category: string, amount: number}[]> => {
  try {
    const sales = await getSales(startDate, endDate);

    // Filter out voided sales
    const activeSales = sales.filter(sale => sale.status !== 'voided');

    // Calculate income by category with proportional discount
    const categoryMap = new Map<string, number>();

    activeSales.forEach(sale => {
      // Calculate subtotal for this sale from items
      const saleSubtotal = sale.items.reduce((sum, item) => sum + item.total, 0);

      // Calculate category subtotals for this sale
      const saleCategoryTotals = new Map<string, number>();

      sale.items.forEach(item => {
        const category = item.type === 'product' ? 'Products' : 'Services';
        const currentTotal = saleCategoryTotals.get(category) || 0;
        saleCategoryTotals.set(category, currentTotal + item.total);
      });

      // Apply proportional discount if exists
      const saleDiscount = sale.discount || 0;
      if (saleDiscount > 0 && saleSubtotal > 0) {
        // Distribute discount proportionally across categories
        saleCategoryTotals.forEach((subtotal, category) => {
          const categoryDiscountAmount = (subtotal / saleSubtotal) * saleDiscount;
          const categoryTotal = subtotal - categoryDiscountAmount;
          const currentAmount = categoryMap.get(category) || 0;
          categoryMap.set(category, currentAmount + categoryTotal);
        });
      } else {
        // No discount, add full amounts
        saleCategoryTotals.forEach((subtotal, category) => {
          const currentAmount = categoryMap.get(category) || 0;
          categoryMap.set(category, currentAmount + subtotal);
        });
      }
    });

    // Convert Map to array
    return Array.from(categoryMap.entries()).map(([category, amount]) => ({
      category,
      amount
    }));
  } catch (error) {
    console.error('Error calculating income by category:', error);
    return [];
  }
};

// Get income by payment method
export const getIncomeByPaymentMethod = async (startDate?: Date, endDate?: Date): Promise<{method: string, amount: number}[]> => {
  try {
    const sales = await getSales(startDate, endDate);
    
    // Filter out voided sales
    const activeSales = sales.filter(sale => sale.status !== 'voided');
    
    // Calculate income by payment method
    const methodMap = new Map<string, number>();
    
    activeSales.forEach(sale => {
      const method = sale.paymentMethod;
      const currentAmount = methodMap.get(method) || 0;
      methodMap.set(method, currentAmount + sale.total);
    });
    
    // Convert Map to array
    return Array.from(methodMap.entries()).map(([method, amount]) => ({
      method,
      amount
    }));
  } catch (error) {
    console.error('Error calculating income by payment method:', error);
    return [];
  }
};

// Subscribe to sales with real-time updates
export const subscribeToSales = (
  callback: (sales: Sale[]) => void,
  startDate?: Date | null,
  endDate?: Date | null
) => {
  try {
    const salesCollection = collection(db, 'sales');
    let salesQuery;

    if (startDate && endDate) {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      salesQuery = query(
        salesCollection,
        where('createdAt', '>=', startTimestamp),
        where('createdAt', '<=', endTimestamp),
        orderBy('createdAt', 'desc')
      );
    } else {
      salesQuery = query(
        salesCollection,
        orderBy('createdAt', 'desc')
      );
    }

    return onSnapshot(salesQuery, (snapshot) => {
      const sales = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : new Date(),
          updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : new Date()
        } as Sale;
      });
      
      callback(sales);
    });
  } catch (error) {
    console.error('Error subscribing to sales:', error);
    return () => {};
  }
};
